import { useState } from 'react'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'

import MoneyField from 'modules/core/components/MoneyField'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { QuotePricingLineItem } from 'modules/finance/components/Quotes/utils'
import RolesAndLevelsEntityPopover from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsEntityPopover'
import { SelectedLevelItem } from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'

interface RoleRowProps {
  item: QuotePricingLineItem
  currency: string
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  locale: string
  onUpdate: (updates: Partial<QuotePricingLineItem>) => void
  onDelete: () => void
}

const RoleRow = ({
  item,
  currency,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  locale,
  onUpdate,
  onDelete,
}: RoleRowProps) => {
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevelItem>()
  const selectedLevelIdFromItem = isJobCatalogTreeEnabled
    ? item.jobCatalogTreeLevelId
    : item.oldJobCatalogLevelId

  const handleUpdateSelectedLevel = (values: { levels?: SelectedLevelItem[] }) => {
    const level = values.levels?.[0]
    if (!level) return

    const levelKey = isJobCatalogTreeEnabled ? 'jobCatalogTreeLevelId' : 'oldJobCatalogLevelId'
    setSelectedLevel(level)
    onUpdate({ [levelKey]: level.id })
  }

  const getDisplayText = () => {
    if (!selectedLevel) {
      if (selectedLevelIdFromItem) return item.description
      return i18n.t('common.select')
    }

    if (selectedLevel.roleName) return `${selectedLevel.roleName} ${selectedLevel.name}`
    return selectedLevel.name
  }

  return (
    <Table.Row>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box width="s300" paddingLeft="s12">
          {isDraftStatus ? (
            <RolesAndLevelsEntityPopover
              selectedLevelIds={selectedLevelIdFromItem ? [selectedLevelIdFromItem] : []}
              isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
              selectorText={getDisplayText()}
              onUpdate={handleUpdateSelectedLevel}
              isSingleSelector
              hideLabel
            />
          ) : (
            <Text size="300" color="grey1000">
              {item.description}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={'h'} // TODO: Translate
                onChange={(value) => onUpdate({ ['quantity']: Number(value) })}
                value={item.quantity.toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {`${item.quantity} h`}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.unitCostCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.unitPriceCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {`${item.baseProfitPercentage}%`}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.baseCostCents,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.basePriceCents,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      {isDraftStatus && (
        <Table.Cell paddingX="s12" colSpan={1} width={80}>
          <Button type="secondary" iconName="delete" onClick={onDelete} />
        </Table.Cell>
      )}
    </Table.Row>
  )
}

export default RoleRow
